<template>
    <div id="medias-container">
        <a-spin :spinning="loading">
            <div class="media-content" v-if="mails && mails.length > 0">
                <div class="media-header">
                    <h2>{{ $t("mail.mediasAttachmentsTitle") }}</h2>
                </div>
                
                <div class="mail-item" v-for="item of mails" :key="item._id">
                    <div class="mail-info">
                        <div class="mail-sender">
                            <span class="sender-avatar">{{ getSenderInitials(item.sender) }}</span>
                            <div class="sender-details">
                                <div class="sender-name">{{ item.sender }}</div>
                                <div class="mail-time">{{ getTime(item.created_at) }}</div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="attachments-gallery">
                        <div v-for="attachment of item.attachments" :key="attachment._id" class="attachment-item">
                            <div v-if="isImageFile(attachment.ext)" class="attachment-card image-card" @click="openAttachment(attachment)">
                                <div class="attachment-preview">
                                    <img :src="attachment.cdnUrl" :alt="attachment.name || $t('mail.mediasUnnamedFile')" />
                                    <div class="file-type-badge">{{ attachment.ext.toUpperCase() }}</div>
                                </div>
                                <div class="attachment-details">
                                    <div class="attachment-name">{{ attachment.name || $t('mail.mediasUnnamedFile') }}</div>
                                    <div class="attachment-meta">
                                        <span class="attachment-size">{{ formatFileSize(attachment.size) }}</span>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="isPdfFile(attachment.ext)" class="attachment-card pdf-card" @click="openAttachment(attachment)">
                                <div class="attachment-preview pdf-preview">
                                    <embed :src="attachment.cdnUrl" type="application/pdf" width="100%" height="100%" />
                                    <div class="file-type-badge">{{ attachment.ext.toUpperCase() }}</div>
                                </div>
                                <div class="attachment-details">
                                    <div class="attachment-name">{{ attachment.name || $t('mail.mediasUnnamedFile') }}</div>
                                    <div class="attachment-meta">
                                        <span class="attachment-size">{{ formatFileSize(attachment.size) }}</span>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="isVideoFile(attachment.ext)" class="attachment-card video-card">
                                <div class="attachment-preview video-preview">
                                    <video controls width="100%" height="100%">
                                        <source :src="attachment.cdnUrl" :type="getVideoMimeType(attachment.ext)">
                                        {{ $t("mail.mediasVideoNotSupported") }}
                                    </video>
                                    <div class="file-type-badge">{{ attachment.ext.toUpperCase() }}</div>
                                </div>
                                <div class="attachment-details">
                                    <div class="attachment-name">{{ attachment.name || $t('mail.mediasUnnamedFile') }}</div>
                                    <div class="attachment-meta">
                                        <span class="attachment-size">{{ formatFileSize(attachment.size) }}</span>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="isAudioFile(attachment.ext)" class="attachment-card audio-card">
                                <div class="attachment-preview audio-preview">
                                    <audio controls style="width: 100%">
                                        <source :src="attachment.cdnUrl" :type="getAudioMimeType(attachment.ext)">
                                        {{ $t("mail.mediasAudioNotSupported") }}
                                    </audio>
                                    <div class="audio-icon">
                                        <i class="material-icons" style="font-size: 48px; color: #1890ff">music_note</i>
                                        <div class="file-type-badge">{{ attachment.ext.toUpperCase() }}</div>
                                    </div>
                                </div>
                                <div class="attachment-details">
                                    <div class="attachment-name">{{ attachment.name || $t('mail.mediasUnnamedFile') }}</div>
                                    <div class="attachment-meta">
                                        <span class="attachment-size">{{ formatFileSize(attachment.size) }}</span>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="isTextFile(attachment.ext)" class="attachment-card text-card" @click="previewTextFile(attachment)">
                                <div class="attachment-preview text-preview">
                                    <div class="text-icon">
                                        <i class="material-icons" style="font-size: 48px; color: #52c41a">description</i>
                                        <div class="file-type-badge">{{ attachment.ext.toUpperCase() }}</div>
                                    </div>
                                </div>
                                <div class="attachment-details">
                                    <div class="attachment-name">{{ attachment.name || $t('mail.mediasUnnamedFile') }}</div>
                                    <div class="attachment-meta">
                                        <span class="attachment-size">{{ formatFileSize(attachment.size) }}</span>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="attachment-card file-card" @click="openAttachment(attachment)">
                                <div class="attachment-preview file-preview">
                                    <div class="file-icon">
                                        <i class="material-icons" style="font-size: 48px; color: #999">visibility_off</i>
                                        <div class="file-type-badge">{{ attachment.ext.toUpperCase() }}</div>
                                    </div>
                                </div>
                                <div class="attachment-details">
                                    <div class="attachment-name">{{ attachment.name || $t('mail.mediasUnnamedFile') }}</div>
                                    <div class="attachment-meta">
                                        <span class="attachment-size">{{ formatFileSize(attachment.size) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="load-more-container">
                    <a-button :loading="loadingMore" @click="loadMore" type="primary" class="load-more-button">
                        {{ $t("mail.mediasLoadMore") }}
                    </a-button>
                </div>
            </div>
            <div class="empty-state" v-else>
                <a-empty :description="$t('mail.mediasNoAttachments')" />
            </div>
        </a-spin>
        
        <a-modal
            v-model="textPreviewVisible"
            :title="currentTextFile ? currentTextFile.filename : $t('mail.mediasFilePreviewTitle')"
            width="700px"
            :footer="null"
        >
            <pre v-if="textPreviewContent" class="text-preview-content">{{ textPreviewContent }}</pre>
            <div v-else class="text-preview-loading">
                <a-spin />
                <div style="margin-top: 10px">{{ $t("mail.mediasFileLoading") }}</div>
            </div>
        </a-modal>
    </div>
</template>

<script>
import { log2 } from "../../../../utils/log2";
import { mapActions, mapState } from "vuex";
import moment from "moment";
import axios from "axios";

const log = log2("Medias");
moment.locale("tr");

export default {
    name: "Medias",
    props: {
        visitorId: {
            type: String,
        },
    },
    data: () => {
        return {
            loading: false,
            loadingMore: false,
            mails: [],
            skip: 0,
            limit: 20,
            textPreviewVisible: false,
            currentTextFile: null,
            textPreviewContent: null,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            allMailAddresses: (state) => state.mail.allMailAddresses,
        }),
    },
    methods: {
        ...mapActions({
            getUserMediaList: "mail/getUserMediaList",
            getDepartmentMediaList: "mail/getDepartmentMediaList",
        }),
        getTime(date) {
            return moment(date).format("LLL");
        },
        isImageFile(ext) {
            return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext.toLowerCase());
        },
        isPdfFile(ext) {
            return ext.toLowerCase() === 'pdf';
        },
        isVideoFile(ext) {
            return ["mp4", "webm", "ogg", "mov", "avi"].includes(ext.toLowerCase());
        },
        isAudioFile(ext) {
            return ["mp3", "wav", "ogg", "m4a"].includes(ext.toLowerCase());
        },
        isTextFile(ext) {
            return ["txt", "log", "md", "html", "css", "js", "json", "xml", "csv"].includes(ext.toLowerCase());
        },
        getVideoMimeType(ext) {
            const types = {
                mp4: 'video/mp4',
                webm: 'video/webm',
                ogg: 'video/ogg',
                mov: 'video/quicktime',
                avi: 'video/x-msvideo'
            };
            return types[ext.toLowerCase()] || 'video/mp4';
        },
        getAudioMimeType(ext) {
            const types = {
                mp3: 'audio/mpeg',
                wav: 'audio/wav',
                ogg: 'audio/ogg',
                m4a: 'audio/mp4'
            };
            return types[ext.toLowerCase()] || 'audio/mpeg';
        },
        formatFileSize(size) {
            if (!size) return "0 B";
            
            const units = ["B", "KB", "MB", "GB"];
            let formattedSize = size;
            let unitIndex = 0;
            
            while (formattedSize >= 1024 && unitIndex < units.length - 1) {
                formattedSize /= 1024;
                unitIndex++;
            }
            
            return `${formattedSize.toFixed(1)} ${units[unitIndex]}`;
        },
        openAttachment(attachment) {
            if (attachment.cdnUrl) {
                window.open(attachment.cdnUrl, '_blank');
            }
        },
        async previewTextFile(attachment) {
            this.textPreviewVisible = true;
            this.currentTextFile = attachment;
            this.textPreviewContent = null;
            
            try {
                const response = await axios.get(attachment.cdnUrl, { responseType: 'text' });
                this.textPreviewContent = response.data;
            } catch (error) {
                console.error('Error loading text file:', error);
                this.textPreviewContent = 'Dosya içeriği yüklenirken bir hata oluştu.';
            }
        },
        getSenderInitials(sender) {
            if (!sender) return '?';
            
            const parts = sender.split('@')[0].split('.');
            if (parts.length >= 2) {
                return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
            } else {
                return sender.charAt(0).toUpperCase();
            }
        },
        getToList(item) {
            return Array.isArray(item.to_list) ? item.to_list : [];
        },
        getCcList(item) {
            return Array.isArray(item.cc_list) ? item.cc_list : [];
        },
        getBccList(item) {
            return Array.isArray(item.bcc_list) ? item.bcc_list : [];
        },
        loadData() {
            const type = this.$route.params.type;
            
            if (type === "user") {
                return this.loadUserData();
            } else {
                return this.loadDepartmentData();
            }
        },
        loadUserData() {
            this.loading = true;
            return this.getUserMediaList({
                mailAddressId: this.$route.params.id,
                skip: this.skip,
                limit: this.limit
            }).then((mails) => {
                if (!mails.length) {
                    if (this.skip > 0) {
                        this.$message.info('Yüklenecek başka medya bulunamadı');
                    }
                } else {
                    if (this.skip > 0) {
                        this.mails = [...this.mails, ...mails];
                    } else {
                        this.mails = mails;
                    }
                }
                this.loading = false;
                this.loadingMore = false;
            }).catch(err => {
                console.error("Error loading user media:", err);
                this.loading = false;
                this.loadingMore = false;
            });
        },
        loadDepartmentData() {
            const departmentMail = this.allMailAddresses.find(
                (email) => email.department && email.department._id === this.$route.params.id
            );
            
            if (!departmentMail) {
                this.loading = false;
                return Promise.resolve();
            }
            
            this.loading = true;
            return this.getDepartmentMediaList({
                departmentName: departmentMail.mail,
                skip: this.skip,
                limit: this.limit
            }).then((mails) => {
                if (!mails.length) {
                    if (this.skip > 0) {
                        this.$message.info('Yüklenecek başka medya bulunamadı');
                    }
                } else {
                    if (this.skip > 0) {
                        this.mails = [...this.mails, ...mails];
                    } else {
                        this.mails = mails;
                    }
                }
                this.loading = false;
                this.loadingMore = false;
            }).catch(err => {
                console.error("Error loading department media:", err);
                this.loading = false;
                this.loadingMore = false;
            });
        },
        loadMore() {
            if (this.loadingMore) return;
            this.skip = this.mails.length;
            this.loadingMore = true;
            this.loadData();
        }
    },
    mounted() {
        this.loadData();
    }
};
</script>

<style lang="scss">
#medias-container {
    padding: 20px;
    background-color: #f9f9f9;
    min-height: calc(100vh - 64px);
    width: 100%;
    
    .media-content {
        max-width: 1200px;
        margin: 0 auto;
        
        .media-header {
            margin-bottom: 24px;
            
            h2 {
                font-size: 24px;
                font-weight: 500;
                color: #333;
                margin: 0;
            }
        }
        
        .mail-item {
            background-color: #fff;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            margin-bottom: 24px;
            overflow: hidden;
            
            .mail-info {
                padding: 16px 24px;
                border-bottom: 1px solid #f0f0f0;
                
                .mail-sender {
                    display: flex;
                    align-items: center;
                    margin-bottom: 12px;
                    
                    .sender-avatar {
                        width: 40px;
                        height: 40px;
                        border-radius: 50%;
                        background-color: #1890ff;
                        color: white;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: 500;
                        margin-right: 12px;
                    }
                    
                    .sender-details {
                        .sender-name {
                            font-weight: 500;
                            font-size: 16px;
                            color: #333;
                        }
                        
                        .mail-time {
                            color: #999;
                            font-size: 13px;
                            margin-top: 2px;
                        }
                    }
                }
                
                .mail-recipients {
                    .recipient-group {
                        display: flex;
                        margin-bottom: 6px;
                        
                        .recipient-type {
                            width: 60px;
                            color: #777;
                            font-weight: 500;
                        }
                        
                        .recipient-list {
                            color: #555;
                            flex: 1;
                            
                            span {
                                margin-right: 4px;
                            }
                        }
                    }
                }
            }
            
            .attachments-gallery {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                gap: 16px;
                padding: 24px;
                
                .attachment-item {
                    .attachment-card {
                        border-radius: 8px;
                        overflow: hidden;
                        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
                        cursor: pointer;
                        transition: all 0.3s ease;
                        height: 100%;
                        
                        &:hover {
                            transform: translateY(-4px);
                            box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
                        }
                        
                        .attachment-details {
                            padding: 12px;
                            background-color: #fff;
                            
                            .attachment-name {
                                font-size: 14px;
                                font-weight: 500;
                                color: #333;
                                margin-bottom: 6px;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                            }
                            
                            .attachment-meta {
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                font-size: 12px;
                                color: #999;
                                
                                .attachment-ext {
                                    background-color: #f5f5f5;
                                    padding: 2px 6px;
                                    border-radius: 4px;
                                    font-weight: 500;
                                }
                            }
                        }
                    }
                    
                    .attachment-preview {
                        position: relative;
                        
                        .file-type-badge {
                            position: absolute;
                            right: 10px;
                            bottom: 10px;
                            font-weight: 600;
                            background: #e6f7ff;
                            color: #1890ff;
                            padding: 4px 8px;
                            border-radius: 4px;
                            font-size: 12px;
                            z-index: 2;
                            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                        }
                    }
                    
                    .image-card {
                        .attachment-preview {
                            height: 160px;
                            background-color: #f8f8f8;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            overflow: hidden;
                            
                            img {
                                width: 100%;
                                height: 100%;
                                object-fit: cover;
                                transition: transform 0.3s ease;
                                
                                &:hover {
                                    transform: scale(1.05);
                                }
                            }
                        }
                    }
                    
                    .pdf-card {
                        .pdf-preview {
                            height: 200px;
                            position: relative;
                            
                            embed {
                                border: none;
                            }
                        }
                    }
                    
                    .video-card {
                        .video-preview {
                            background-color: #000;
                            height: 180px;
                            position: relative;
                        }
                    }
                    
                    .audio-card {
                        .audio-preview {
                            background-color: #f8f8f8;
                            height: 180px;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            padding: 15px;
                            position: relative;
                            
                            .audio-icon {
                                margin-top: 15px;
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                justify-content: center;
                            }
                        }
                    }
                    
                    .text-card {
                        .text-preview {
                            height: 160px;
                            background-color: #f8f8f8;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            position: relative;
                            
                            .text-icon {
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                justify-content: center;
                            }
                        }
                    }
                    
                    .file-card {
                        .file-preview {
                            height: 160px;
                            background-color: #f8f8f8;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            position: relative;
                            
                            .file-icon {
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                justify-content: center;
                            }
                        }
                        
                        .attachment-details {
                            flex: 1;
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                        }
                    }
                }
            }
        }
        
        .load-more-container {
            display: flex;
            justify-content: center;
            margin: 30px 0;
            
            .load-more-button {
                padding: 0 30px;
                height: 40px;
                font-size: 15px;
                border-radius: 20px;
            }
        }
    }
    
    .empty-state {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 300px;
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
}

.text-preview-content {
    max-height: 500px;
    overflow: auto;
    padding: 15px;
    border-radius: 4px;
    background-color: #f5f5f5;
    white-space: pre-wrap;
    font-family: monospace;
    font-size: 13px;
    line-height: 1.5;
}

.text-preview-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
}
</style>
